/**
 * ViewerPage (F2) — public, no-auth audience page mounted at `/viewer/:token`.
 *
 * Connects to the viewer downlink (`/ws/viewer/{token}`) and renders live
 * subtitles into its OWN isolated subtitle store instance, so multiple viewers
 * (and the host) never share line state. Handles:
 *  - live connection status (connecting / connected / reconnecting / closed);
 *  - a graceful "link invalid or expired" terminal state when the server closes
 *    with code 4404.
 *
 * F3: viewers also play `tts.chunk` audio through a shared {@link TtsPlayer}
 * (sequential, no overlap). Playback starts MUTED — a viewer may only want
 * subtitles, and browser autoplay policies block unsolicited audio — with a
 * mute toggle to opt in. An `ops.alert(tts_contingency)` surfaces a
 * subtitle-only notice.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useParams } from 'react-router-dom';
import { SubtitleView } from '@/features/subtitles/SubtitleView';
import { createSubtitleStore } from '@/features/subtitles/subtitleStore';
import { openViewerConnection } from '@/features/viewer/viewerApi';
import { useTtsPlayback, type EventSubscribe } from '@/features/tts/useTtsPlayback';
import type { EventEnvelopeV1 } from '@/shared/models/events';
import type { WsStatus } from '@/shared/ws/wsClient';

const STATUS_LABEL: Record<WsStatus, string> = {
  idle: 'Ocioso',
  connecting: 'Conectando…',
  open: 'Ao vivo',
  closed: 'Desconectado',
  reconnecting: 'Reconectando…',
};

function statusColor(status: WsStatus): string {
  if (status === 'open') return 'var(--rt-success)';
  if (status === 'connecting' || status === 'reconnecting')
    return 'var(--rt-text-muted)';
  return 'var(--rt-danger)';
}

export function ViewerPage(): ReactElement {
  const { token } = useParams<{ token: string }>();

  // One isolated subtitle store per mounted viewer page.
  const subtitleStore = useMemo(() => createSubtitleStore(), []);

  const [status, setStatus] = useState<WsStatus>('idle');
  const [linkInvalid, setLinkInvalid] = useState(false);
  const invalidRef = useRef(false);

  // Fan-out of live WS events so the TTS hook can subscribe with a stable fn.
  const listenersRef = useRef(new Set<(event: EventEnvelopeV1) => void>());
  const subscribe = useCallback<EventSubscribe>((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  // Viewers start muted (subtitles only by default; opt into audio).
  const tts = useTtsPlayback({ subscribe, initialMuted: true });

  useEffect(() => {
    if (!token) {
      setLinkInvalid(true);
      return;
    }

    const ingest = subtitleStore.getState().ingest;
    const connection = openViewerConnection(token, {
      onStatus: setStatus,
      onLinkInvalid: () => {
        invalidRef.current = true;
        setLinkInvalid(true);
      },
      onError: () => {
        /* transient socket errors surface via status/reconnect */
      },
    });

    const unsubscribe = connection.onEvent((event) => {
      listenersRef.current.forEach((fn) => fn(event));
      if (
        event.event_type === 'translation.partial' ||
        event.event_type === 'translation.final'
      ) {
        ingest(event);
      }
    });

    // Close the socket on tab/browser close or reload too — React cleanup only
    // fires on SPA unmount, not on these page-exit paths.
    const closeOnExit = (): void => {
      connection.close();
    };
    window.addEventListener('pagehide', closeOnExit);
    window.addEventListener('beforeunload', closeOnExit);

    return () => {
      window.removeEventListener('pagehide', closeOnExit);
      window.removeEventListener('beforeunload', closeOnExit);
      unsubscribe();
      connection.close();
    };
  }, [token, subtitleStore]);

  if (linkInvalid) {
    return (
      <main style={styles.page}>
        <div style={styles.invalidCard}>
          <h1 style={styles.invalidTitle}>Link inválido ou expirado</h1>
          <p style={styles.invalidText}>
            Este link de audiência não é mais válido. Peça ao organizador um novo
            link para acompanhar as legendas ao vivo.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <strong style={styles.brand}>RealtimeTranslator</strong>
        <div style={styles.headerControls}>
          {tts.isContingency ? (
            <span style={styles.contingency} role="alert">
              Somente legendas
            </span>
          ) : (
            <button
              type="button"
              onClick={tts.toggleMuted}
              style={styles.audioButton}
              aria-pressed={!tts.muted}
            >
              {tts.muted ? '🔇 Ativar áudio' : '🔊 Áudio ativo'}
            </button>
          )}
          <span
            style={{ ...styles.statusPill, color: statusColor(status) }}
            aria-live="polite"
          >
            <span
              style={{ ...styles.dot, background: statusColor(status) }}
              aria-hidden
            />
            {STATUS_LABEL[status]}
          </span>
        </div>
      </header>

      <section style={styles.subtitleWrap}>
        <SubtitleView
          store={subtitleStore}
          emptyLabel="Aguardando o início da apresentação…"
        />
      </section>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '0.75rem 1.25rem',
    borderBottom: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
  },
  brand: { fontSize: '0.95rem' },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  audioButton: {
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    borderRadius: 999,
    padding: '0.2rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  contingency: {
    fontSize: '0.8rem',
    padding: '0.2rem 0.6rem',
    borderRadius: 999,
    border: '1px solid var(--rt-danger)',
    color: 'var(--rt-danger)',
    whiteSpace: 'nowrap',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.8rem',
    padding: '0.2rem 0.6rem',
    borderRadius: 999,
    border: '1px solid var(--rt-border)',
    whiteSpace: 'nowrap',
  },
  dot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
  subtitleWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '1.25rem',
    maxWidth: 960,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  invalidCard: {
    margin: 'auto',
    maxWidth: 420,
    textAlign: 'center',
    padding: '2rem',
    background: 'var(--rt-surface)',
    border: '1px solid var(--rt-border)',
    borderRadius: 12,
  },
  invalidTitle: { margin: '0 0 0.75rem', fontSize: '1.3rem' },
  invalidText: { margin: 0, color: 'var(--rt-text-muted)', lineHeight: 1.5 },
};
